import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";
import VersionedComponent from "@components/VersionedComponent";
import { VERSION_CM_12923_LIST_SEARCHBAR_FILTER, VERSION_CM_13766_SHARES_V3 } from "@cores/version";
import { PageHeader } from "@features/page-header/page-header";
import Typography from "@components_v2/typography/typography";
import { Box, Divider, Grid } from "@mzc-pdc/ui";
import { SEARCH_FILTER_TYPE, TABLE_LIST_TYPE } from "@constants";
import { withSearchQueryParamsContext } from "@components/params";
import * as yup from "yup";
import ResponsiveSearchFilters from "@components_v2/search-filters/responsive-search-filters";
import theme from "@styles/theme";
import { Tabs } from "@components_v2/tabs/tabs";
import ListSharedAssetsByMe from "@routes/shared-v2/list-shared-assets-by-me";
import ListSharedAssetsWithMe from "@routes/shared-v2/list-shared-assets-with-me";

const ListMySharedAssets = ({ searchQueryParams }) => {
    const { spaceId, projectId } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [editFilterValue, setEditFilterValue] = useState({});
    const [filterOptionValues, setFilterOptionValues] = useState({});
    const [activeTab, setActiveTab] = useState(searchQueryParams.params.type ?? "SHARED_WITH_ME");

    const handleChangeFilterValue = useCallback(
        (values) => {
            setEditFilterValue(values);
            searchQueryParams.updateSearchQueryParams({
                action: "push",
                params: { ...searchQueryParams.params, ...values },
            });
        },
        [searchQueryParams],
    );

    const onHandleClickReset = useCallback(() => {
        setEditFilterValue({});
        const filterIds = Object.keys(filterOptionValues);
        const params = { ...searchQueryParams.params };
        filterIds.forEach((filterId) => {
            params[filterId] = undefined;
        });

        searchQueryParams.resetSearchQueryParams({
            action: "push",
            params: { ...params },
        });
    }, [searchQueryParams]);

    return (
        <VersionedComponent version={VERSION_CM_13766_SHARES_V3}>
            <>
                <PageHeader
                    title={
                        <Typography variant={"h3"} color={theme.palette.greySecondary.main}>
                            {t("common::label::Share", "Share")}
                        </Typography>
                    }
                />
                <Grid container sx={{ flex: 1, overflow: `hidden` }} flexDirection={"row"} flexWrap={"nowrap"}>
                    <Grid item xs pt={1.5} sx={{ display: `flex`, flexDirection: `column`, overflow: `hidden` }}>
                        <VersionedComponent version={VERSION_CM_12923_LIST_SEARCHBAR_FILTER}>
                            <Grid item mb={theme.spacing(2)}>
                                <ResponsiveSearchFilters
                                    domain={TABLE_LIST_TYPE.MY_SHARED_ASSETS}
                                    type={SEARCH_FILTER_TYPE.SEARCH_QUERY_PARAMS}
                                    optionValues={filterOptionValues}
                                    values={{ ...searchQueryParams.params }}
                                    onClickSubmit={handleChangeFilterValue}
                                    onClickReset={onHandleClickReset}
                                />
                            </Grid>
                        </VersionedComponent>
                        <Box sx={{ height: 44 }}>
                            <Tabs
                                fullWidth={true}
                                value={activeTab}
                                onChangeTab={(tab) => setActiveTab(tab)}
                                indicatorColor="secondary"
                                isBordered={true}
                                indicatorPosition={"top"}
                                textColor={"secondary"}
                                items={[
                                    {
                                        label: t(`common::label::Shared with me`),
                                        value: "SHARED_WITH_ME",
                                    },
                                    {
                                        label: t(`common::label::Shared by me`),
                                        value: "SHARED_BY_ME",
                                    },
                                ]}
                            />
                        </Box>
                        <Divider sx={{ borderColor: theme.palette.greySecondary.main }} />
                        {activeTab === "SHARED_WITH_ME" && (
                            <ListSharedAssetsWithMe
                                updateFilterValueOptions={(options) => setFilterOptionValues(options)}
                            />
                        )}
                        {activeTab === "SHARED_BY_ME" && (
                            <ListSharedAssetsByMe
                                updateFilterValueOptions={(options) => setFilterOptionValues(options)}
                            />
                        )}
                    </Grid>
                </Grid>
            </>
        </VersionedComponent>
    );
};

export default withSearchQueryParamsContext({
    addedSchema: {
        projectIds: yup.array(),
        type: yup.string(),
    },
    setProps: true,
})(ListMySharedAssets);
